import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Home, Loader2, Search, User, Phone, Mail,
  Car, Check, Sparkles, ShieldCheck, Wrench, Stethoscope, NotebookPen,
  RefreshCw,
} from "lucide-react";

import Header from "@/components/shared/Header";
import { Button } from "@/components/ui/button";
import { API } from "@/contexts/AuthContext";

import IntakeStepper from "@/components/intake/companion/IntakeStepper";
import FOHGuidancePanel from "@/components/intake/companion/FOHGuidancePanel";
import MOTCompanion from "@/components/intake/companion/MOTCompanion";
import ServiceCompanion from "@/components/intake/companion/ServiceCompanion";
import DiagnosticCompanion from "@/components/intake/companion/DiagnosticCompanion";
import CustomerIntakeSummary from "@/components/intake/companion/CustomerIntakeSummary";
import { GHSearchModal } from "@/components/intake/GHSearchModal";  // iter212c

/**
 * Yestimate Intake Companion — Phase 1.
 *
 * Single entry point that replaces the legacy "Create Booking" + "Request VI"
 * flows. Walks the FOH through three stages:
 *
 *   1. Customer  — name / phone / email
 *   2. Vehicle   — reg + DVLA lookup
 *   3. Reason    — multi-select work types (MOT/Service/Diag)
 *                  + relevant Companion panels (MOT / Service / Diag)
 *                  + deep Warning Light fault-tree inside Diagnostic
 *
 * On completion, POSTs to /api/intake/sessions with `services_requested`
 * so the existing backend multi-service allocation engine routes parallel
 * MCC (Tester) + VI (Tech) allocations correctly.
 */
const STAGES = [
  { id: "customer", label: "Customer", sublabel: "Who is calling" },
  { id: "vehicle", label: "Vehicle", sublabel: "What they drive" },
  { id: "reason", label: "Reason", sublabel: "Why they're calling" },
  { id: "review", label: "Review", sublabel: "Confirm + submit" },
];

export default function StartIntakePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stageIdx, setStageIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // iter211aw — Garage Hive import modal state
  // iter213b — auto-open the GH search when a deep-link routes here with
  // `?ghSearch=1&q=<query>`. This lets /gh/open reuse the same universal
  // search component instead of duplicating it.
  const initialGHOpen = searchParams.get("ghSearch") === "1";
  const initialGHQuery = searchParams.get("q") || "";
  const [showGHImport, setShowGHImport] = useState(initialGHOpen);
  const [ghInitialQuery, setGhInitialQuery] = useState(initialGHQuery);

  useEffect(() => {
    if (initialGHOpen) {
      setShowGHImport(true);
      setGhInitialQuery(initialGHQuery);
      // Clean the URL so a refresh doesn't re-open the modal after close.
      const next = new URLSearchParams(searchParams);
      next.delete("ghSearch");
      next.delete("q");
      setSearchParams(next, { replace: true });
    }
  }, []);

  // Customer
  const [customer, setCustomer] = useState({
    customer_name: "",
    contact_number: "",
    customer_email: "",
  });

  // Vehicle
  const [vehicle, setVehicle] = useState({
    vehicle_reg: "",
    make: null,
    model: null,
    year: null,
    fuel_type: null,
    colour: null,
    mot_expiry_date: null,
    mot_status: null,
    // iter204 — Vehicle physical state captured at intake. Drives the
    // tester / technician workshop cards so they know whether to start
    // work or wait for the keys to arrive.
    vehicle_on_site: false,
    key_tag_number: "",
  });
  const [lookup, setLookup] = useState({ loading: false, error: null, found: false });

  // Reason
  const [services, setServices] = useState({
    mot: false,
    service: false,
    diag: false,
  });
  const [reasonText, setReasonText] = useState("");
  const [motData, setMotData] = useState({});
  const [serviceData, setServiceData] = useState({});
  const [diagData, setDiagData] = useState({});

  // ---- Validation -----------------------------------------------------------

  const canAdvance = () => {
    if (stageIdx === 0)
      return (customer.customer_name || "").trim().length > 0;
    if (stageIdx === 1) return (vehicle.vehicle_reg || "").trim().length > 0;
    if (stageIdx === 2)
      return services.mot || services.service || services.diag;
    // Stage 3 (review) — only requires that we reached it with at least 1 service.
    if (stageIdx === 3)
      return services.mot || services.service || services.diag;
    return false;
  };

  // ---- DVLA lookup ---------------------------------------------------------

  const handleRegLookup = async () => {
    const reg = (vehicle.vehicle_reg || "").trim();
    if (!reg) return;
    setLookup({ loading: true, error: null, found: false });
    try {
      const res = await axios.get(
        `${API}/intake/vehicle-lookup/${encodeURIComponent(reg)}`
      );
      if (res.data?.found) {
        const v = res.data;
        const cleanModel = (() => {
          const m = String(v.model || "").trim();
          if (!m || m.toLowerCase() === "unknown") return "";
          return m;
        })();
        setVehicle((prev) => ({
          ...prev,
          make: v.make || null,
          model: cleanModel || null,
          year: v.year || null,
          fuel_type: v.fuel_type || null,
          colour: v.colour || null,
          mot_expiry_date: v.mot_expiry_date || null,
          mot_status: v.mot_status || null,
        }));
        setLookup({ loading: false, error: null, found: true });
      } else {
        setLookup({
          loading: false,
          error: res.data?.reason || "No DVLA record for this registration",
          found: false,
        });
      }
    } catch (err) {
      setLookup({
        loading: false,
        error: err?.response?.data?.detail || "DVLA lookup failed",
        found: false,
      });
    }
  };

  const setVehicleReg = (val) => {
    setVehicle((prev) => ({
      ...prev,
      vehicle_reg: val.toUpperCase(),
      ...(lookup.found
        ? { make: null, model: null, year: null, fuel_type: null, colour: null }
        : {}),
    }));
    if (lookup.found || lookup.error) {
      setLookup({ loading: false, error: null, found: false });
    }
  };

  // ---- Submit --------------------------------------------------------------

  // Compute the primary journey_id from selected work types. The backend
  // allocation engine still receives the full `services_requested` array so
  // parallel allocations land correctly — the journey_id just steers the
  // question-tree that surfaces inside the IntakeSessionPage workspace.
  const computeJourneyId = () => {
    const { mot, service, diag } = services;
    if (mot && diag) return "mot_plus_concern";
    if (mot && service) return "mot";
    if (mot) return "mot";
    if (diag) return "diagnostic";
    if (service) return "service";
    return "other";
  };

  const handleComplete = async () => {
    if (!canAdvance()) {
      toast.error("Pick at least one work type to start the intake.");
      return;
    }
    setSubmitting(true);
    try {
      const services_requested = [];
      if (services.mot) services_requested.push("mot");
      if (services.service) services_requested.push("service");
      if (services.diag) services_requested.push("diag");

      // FOH-captured reason notes get appended into foh_notes so they
      // surface immediately on the session workspace.
      const compositeNotes = composeFohNotes({
        reasonText,
        services,
        motData,
        serviceData,
        diagData,
      });

      const payload = {
        customer_name: customer.customer_name,
        vehicle_reg: vehicle.vehicle_reg,
        contact_number: customer.contact_number,
        customer_email: customer.customer_email,
        make: vehicle.make,
        model: vehicle.model,
        year: vehicle.year,
        fuel_type: vehicle.fuel_type,
        colour: vehicle.colour,
        mot_expiry_date: vehicle.mot_expiry_date,
        mot_status: vehicle.mot_status,
        // iter204 — Physical state at intake. Bool + key tag flow to the
        // workshop tester/tech cards via the auto-handoff.
        vehicle_on_site: !!vehicle.vehicle_on_site,
        key_tag_number: vehicle.vehicle_on_site
          ? (vehicle.key_tag_number || "").trim() || null
          : null,
        journey_id: computeJourneyId(),
        services_requested,
        foh_notes: compositeNotes,
        // Phase 2 — also send the per-service stage-3 capture as
        // structured fields so the tester / technician workspaces can
        // surface them natively (especially mot_capture.last_advisories,
        // which the tester reads pre-test).
        mot_capture: services.mot && Object.keys(motData || {}).length ? motData : null,
        service_capture:
          services.service && Object.keys(serviceData || {}).length ? serviceData : null,
        diag_capture:
          services.diag && Object.keys(diagData || {}).length ? diagData : null,
      };

      const res = await axios.post(`${API}/intake/sessions`, payload);
      const intakeId = res.data?.id;
      // Phase 2 — hard-skip the legacy IntakeSessionPage workspace. Stage 1-4
      // already captured everything the old question-engine asked for, so we
      // drop the FOH straight back on the dashboard with a confirmation toast.
      // The backend has already created the parallel allocations (MCC → Tester
      // dash, VI → Tech dash) so the workshop is fully briefed without the
      // FOH walking through any more screens.
      const summaryBits = [];
      if (services.mot) summaryBits.push("MOT");
      if (services.service) summaryBits.push("Service");
      if (services.diag) summaryBits.push("Diagnostic");
      const headline = summaryBits.join(" + ") || "Intake";
      const firstName = (customer.customer_name || "").trim().split(/\s+/)[0];
      toast.success(
        firstName
          ? `${headline} booked for ${firstName} — workshop notified.`
          : `${headline} booked — workshop notified.`,
        { duration: 5000 }
      );
      navigate("/app/foh", {
        state: { lastIntakeId: intakeId, lastIntakeHeadline: headline },
      });
    } catch (err) {
      // Backend may return either a plain string detail (FastAPI default)
      // or a structured dict like {code, message, existing_job_id, ...}
      // for the duplicate-active-job conflict. Surface a readable message
      // in both cases instead of dumping "[object Object]" on the FOH.
      const detail = err?.response?.data?.detail;
      let msg;
      if (typeof detail === "string") {
        msg = detail;
      } else if (detail && typeof detail === "object") {
        msg = detail.message || detail.code || "Could not start intake";
      } else {
        msg = "Could not start intake";
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Render --------------------------------------------------------------

  return (
    <div className="min-h-screen bg-slate-50 relative overflow-hidden">
      {/* Decorative gradient — matches Yestimate Intake list aesthetic */}
      <div className="pointer-events-none absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-gradient-to-br from-teal-200/30 via-emerald-200/20 to-transparent blur-3xl" />
      <div className="pointer-events-none absolute top-40 -left-32 w-[420px] h-[420px] rounded-full bg-gradient-to-br from-blue-200/20 via-indigo-200/10 to-transparent blur-3xl" />

      <Header />

      <div className="relative max-w-6xl mx-auto px-5 sm:px-6 py-6">
        {/* Page header */}
        <div className="flex justify-between items-end mb-5 gap-3">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-1.5 mb-1.5 px-2 py-0.5 rounded-full bg-teal-100/70 border border-teal-200/70">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-600" />
              <p className="text-[10px] uppercase tracking-[0.22em] text-teal-800 font-semibold">
                Front-of-House · Intake Companion
              </p>
            </div>
            <h1 className="text-[28px] sm:text-[32px] font-bold tracking-tight bg-gradient-to-r from-slate-900 via-slate-800 to-teal-800 bg-clip-text text-transparent leading-tight">
              Start Intake
            </h1>
            <p className="text-slate-600 mt-1.5 text-[14.5px] leading-relaxed max-w-xl">
              Walk the customer through three calm stages — the workshop will
              be ready before they hang up.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* iter211aw — Garage Hive import launcher. Opens a modal
                where FOH types a jobsheet number and the intake
                populates automatically from the GH source of truth. */}
            <Button
              onClick={() => setShowGHImport(true)}
              variant="outline"
              className="border-teal-300 text-teal-700 hover:bg-teal-50"
              data-testid="gh-import-btn"
            >
              Import from Garage Hive
            </Button>
            <Button
              onClick={() => navigate("/app/foh")}
              variant="outline"
              className="border-slate-300 hover:bg-slate-50"
              data-testid="intake-companion-exit-btn"
            >
              <Home className="w-4 h-4 mr-1.5" />
              FOH Dashboard
            </Button>
          </div>
        </div>

        {/* Stepper */}
        <div className="mb-4">
          <IntakeStepper
            stages={STAGES}
            currentIndex={stageIdx}
            onJumpBack={(i) => setStageIdx(i)}
          />
        </div>

        {/* Stage body */}
        {stageIdx === 0 && (
          <StageCustomer
            data={customer}
            onChange={(patch) => setCustomer((p) => ({ ...p, ...patch }))}
          />
        )}
        {stageIdx === 1 && (
          <StageVehicle
            data={vehicle}
            lookup={lookup}
            onChangeReg={setVehicleReg}
            onChange={(patch) => setVehicle((p) => ({ ...p, ...patch }))}
            onLookup={handleRegLookup}
          />
        )}
        {stageIdx === 2 && (
          <StageReason
            services={services}
            onToggleService={(key) =>
              setServices((s) => ({ ...s, [key]: !s[key] }))
            }
            reasonText={reasonText}
            onReasonText={setReasonText}
            vehicleReg={vehicle.vehicle_reg}
            motData={motData}
            onMotChange={(patch) => setMotData((d) => ({ ...d, ...patch }))}
            serviceData={serviceData}
            onServiceChange={(patch) =>
              setServiceData((d) => ({ ...d, ...patch }))
            }
            diagData={diagData}
            onDiagChange={(patch) =>
              setDiagData((d) => ({ ...d, ...patch }))
            }
          />
        )}
        {stageIdx === 3 && (
          <StageReview
            customer={customer}
            vehicle={vehicle}
            services={services}
            reasonText={reasonText}
            motData={motData}
            serviceData={serviceData}
            diagData={diagData}
          />
        )}

        {/* Footer nav */}
        <div className="mt-5 flex items-center justify-between gap-3">
          <Button
            variant="outline"
            onClick={() =>
              stageIdx === 0 ? navigate("/app/foh") : setStageIdx(stageIdx - 1)
            }
            className="border-slate-300"
            data-testid="intake-companion-back"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            {stageIdx === 0 ? "Cancel" : "Back"}
          </Button>

          {stageIdx < STAGES.length - 1 ? (
            <Button
              onClick={() => canAdvance() && setStageIdx(stageIdx + 1)}
              disabled={!canAdvance()}
              className="bg-teal-700 hover:bg-teal-800 text-white px-5"
              data-testid="intake-companion-next"
            >
              Next — {STAGES[stageIdx + 1].label}
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          ) : (
            <Button
              onClick={handleComplete}
              disabled={!canAdvance() || submitting}
              className="bg-slate-900 hover:bg-slate-800 text-white px-5"
              data-testid="intake-companion-complete"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Start intake session
            </Button>
          )}
        </div>
      </div>

      {/* iter212c — Garage Hive universal search modal */}
      {showGHImport && (
        <GHSearchModal
          isOpen={showGHImport}
          onClose={() => { setShowGHImport(false); setGhInitialQuery(""); }}
          initialQuery={ghInitialQuery}
          onImported={(payload) => {
            setShowGHImport(false);
            setGhInitialQuery("");
            const sid = payload?.intake_session_id;
            if (sid) navigate(`/app/foh/intake/gh-case/${sid}`);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// STAGE 1 — Customer
// ---------------------------------------------------------------------------

function StageCustomer({ data, onChange }) {
  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4"
      data-testid="stage-customer"
    >
      <div className="space-y-3">
        <StageHeader
          tone="blue"
          icon={<User className="w-4 h-4" />}
          eyebrow="Stage 1 of 3"
          title="Who are we talking to?"
          subtitle="Just the basics — we'll grab vehicle and reason next."
        />

        <section className="rounded-lg border border-slate-200 bg-white p-5 space-y-4">
          <FieldShell label="Customer name" icon={<User className="w-4 h-4" />} required>
            <input
              type="text"
              autoFocus
              placeholder="Full name"
              value={data.customer_name}
              onChange={(e) => onChange({ customer_name: e.target.value })}
              className="w-full bg-transparent text-[17px] text-slate-900 placeholder:text-slate-400 focus:outline-none"
              data-testid="stage-customer-name"
            />
          </FieldShell>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FieldShell label="Contact number" icon={<Phone className="w-4 h-4" />}>
              <input
                type="tel"
                placeholder="Mobile preferred"
                value={data.contact_number}
                onChange={(e) => onChange({ contact_number: e.target.value })}
                className="w-full bg-transparent text-[17px] text-slate-900 placeholder:text-slate-400 focus:outline-none"
                data-testid="stage-customer-phone"
              />
            </FieldShell>

            <FieldShell label="Email" icon={<Mail className="w-4 h-4" />}>
              <input
                type="email"
                placeholder="customer@email.com"
                value={data.customer_email}
                onChange={(e) => onChange({ customer_email: e.target.value })}
                className="w-full bg-transparent text-[17px] text-slate-900 placeholder:text-slate-400 focus:outline-none"
                data-testid="stage-customer-email"
              />
            </FieldShell>
          </div>
        </section>
      </div>

      <FOHGuidancePanel
        tone="teal"
        title="Opening guidance"
        askNext={[
          "Get the customer's name first — calmer call from sentence one.",
          "Mobile number is more useful than landline — for SMS updates later.",
        ]}
        sayToCustomer={[
          "Of course — let me take a few details so I can pull everything up while we talk.",
        ]}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// STAGE 2 — Vehicle
// ---------------------------------------------------------------------------

function StageVehicle({ data, lookup, onChangeReg, onChange, onLookup }) {
  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4"
      data-testid="stage-vehicle"
    >
      <div className="space-y-3">
        <StageHeader
          tone="teal"
          icon={<Car className="w-4 h-4" />}
          eyebrow="Stage 2 of 3"
          title="Which vehicle?"
          subtitle="Reg + DVLA lookup — saves asking the customer questions we can answer ourselves."
        />

        <section className="rounded-lg border border-slate-200 bg-white p-5 space-y-3.5">
          <FieldShell
            label="Vehicle reg"
            icon={<Car className="w-4 h-4" />}
            required
            accessory={
              <button
                type="button"
                onClick={onLookup}
                disabled={!data.vehicle_reg?.trim() || lookup.loading}
                className="inline-flex items-center gap-1 text-[12.5px] text-teal-700 hover:text-teal-900 disabled:opacity-40 font-semibold"
                data-testid="stage-vehicle-lookup-btn"
              >
                {lookup.loading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Search className="w-3.5 h-3.5" />
                )}
                Lookup
              </button>
            }
          >
            <input
              type="text"
              autoFocus
              placeholder="AB12 CDE"
              value={data.vehicle_reg}
              onChange={(e) => onChangeReg(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && data.vehicle_reg?.trim()) {
                  e.preventDefault();
                  onLookup();
                }
              }}
              className="w-full bg-transparent text-[18px] text-slate-900 placeholder:text-slate-400 placeholder:normal-case font-mono tracking-wider uppercase focus:outline-none"
              data-testid="stage-vehicle-reg"
            />
          </FieldShell>

          {lookup.found && (
            <div
              className="rounded-md border border-emerald-200 bg-emerald-50/60 px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-[14px]"
              data-testid="stage-vehicle-dvla-card"
            >
              <span className="inline-flex items-center text-emerald-700 text-[11.5px] uppercase tracking-[0.18em] font-semibold">
                <Check className="w-4 h-4 mr-1" />
                DVLA match
              </span>
              <VehicleField label="Make" value={data.make} />
              <span className="inline-flex items-baseline gap-1.5">
                <span className="text-[11.5px] uppercase tracking-wider text-slate-500">
                  Model
                </span>
                <input
                  type="text"
                  className="text-slate-900 font-semibold bg-white border border-emerald-200 rounded px-2 py-0.5 w-44 text-[14px] focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  value={data.model || ""}
                  placeholder="Enter model"
                  onChange={(e) => onChange({ model: e.target.value })}
                  data-testid="stage-vehicle-model-input"
                />
              </span>
              <VehicleField label="Year" value={data.year} />
              <VehicleField label="Fuel" value={data.fuel_type} />
              <VehicleField label="Colour" value={data.colour} />
              {data.mot_expiry_date && (
                <span className="inline-flex items-baseline gap-1.5 ml-auto px-2.5 py-1 rounded bg-white border border-emerald-300">
                  <span className="text-[11.5px] uppercase tracking-wider text-emerald-700 font-semibold">
                    MOT expires
                  </span>
                  <span className="text-emerald-900 font-bold">
                    {formatMotDate(data.mot_expiry_date)}
                  </span>
                </span>
              )}
            </div>
          )}

          {lookup.error && !lookup.found && (
            <div
              className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-[13.5px] text-amber-900"
              data-testid="stage-vehicle-lookup-error"
            >
              {lookup.error} — you can still continue without lookup.
            </div>
          )}
        </section>

        {/* iter204 — Vehicle on-site + Key tag. Captured at intake so
            workshop tester/tech cards know whether to start work or
            wait for the keys/vehicle to arrive. Keytag input only
            appears when the toggle is on (no point asking when the
            vehicle is still at the customer's home). */}
        <section
          className="rounded-lg border border-slate-200 bg-white p-5 space-y-3"
          data-testid="stage-vehicle-onsite-section"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[13px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                Physical handover
              </p>
              <h3 className="text-[16px] font-semibold text-slate-900 mt-0.5">
                Is the vehicle already here?
              </h3>
              <p className="text-[13px] text-slate-600 mt-1 leading-snug">
                If yes, capture the key-tag number so the workshop can find
                the keys at a glance.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={!!data.vehicle_on_site}
              onClick={() =>
                onChange({
                  vehicle_on_site: !data.vehicle_on_site,
                  // Clear keytag when toggling off so we don't persist
                  // a stale value.
                  ...(data.vehicle_on_site ? { key_tag_number: "" } : {}),
                })
              }
              data-testid="stage-vehicle-onsite-toggle"
              className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 ${
                data.vehicle_on_site ? "bg-teal-600" : "bg-slate-300"
              }`}
            >
              <span
                aria-hidden="true"
                className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  data.vehicle_on_site ? "translate-x-5" : "translate-x-0.5"
                } self-center`}
              />
            </button>
          </div>

          {data.vehicle_on_site && (
            <div
              className="rounded-md border border-teal-200 bg-teal-50/60 px-3 py-2.5"
              data-testid="stage-vehicle-keytag-wrapper"
            >
              <label
                htmlFor="stage-vehicle-keytag"
                className="text-[11.5px] uppercase tracking-wider text-teal-800 font-semibold"
              >
                Key-tag number
              </label>
              <input
                id="stage-vehicle-keytag"
                type="text"
                placeholder="e.g. 042"
                value={data.key_tag_number || ""}
                onChange={(e) =>
                  onChange({ key_tag_number: e.target.value })
                }
                className="mt-1 w-full bg-white border border-teal-200 rounded px-2.5 py-1.5 text-[14.5px] font-mono tracking-wider text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                data-testid="stage-vehicle-keytag-input"
              />
              <p className="text-[11.5px] text-teal-700 mt-1.5">
                Optional — leave blank if the customer hasn&apos;t tagged the keys yet.
              </p>
            </div>
          )}
        </section>
      </div>

      <FOHGuidancePanel
        tone="teal"
        title="Vehicle guidance"
        askNext={[
          "Reg first — DVLA fills in make / model / MOT expiry so you don't have to ask.",
          "If DVLA returns ‘Unknown’ for the model, just type the trim — customer usually knows.",
          "Ask if the vehicle is already with you — if yes, drop the key-tag number so the workshop knows the keys are on the board.",
        ]}
        sayToCustomer={[
          "Just pop the registration over — I'll pull your MOT history up while we talk.",
          "Have you dropped the car off, or is it still with you?",
        ]}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// STAGE 3 — Reason (multi-select + companions)
// ---------------------------------------------------------------------------

function StageReason({
  services,
  onToggleService,
  reasonText,
  onReasonText,
  vehicleReg,
  motData,
  onMotChange,
  serviceData,
  onServiceChange,
  diagData,
  onDiagChange,
}) {
  return (
    <div className="space-y-4" data-testid="stage-reason">
      <StageHeader
        tone="amber"
        icon={<NotebookPen className="w-4 h-4" />}
        eyebrow="Stage 3 of 3"
        title="Why are they calling?"
        subtitle="Pick all that apply — combos route parallel allocations (Tester + Tech) automatically."
      />

      {/* Work type multi-select */}
      <section className="rounded-lg border border-slate-200 bg-white p-5">
        <p className="text-[12.5px] uppercase tracking-[0.18em] text-slate-700 font-semibold mb-3">
          Work type
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="reason-work-types">
          <WorkTypeCard
            id="mot"
            active={services.mot}
            onToggle={() => onToggleService("mot")}
            icon={<ShieldCheck className="w-5 h-5" />}
            tone="blue"
            label="MOT"
            sublabel="Annual test"
            hint="Routes to Tester Dashboard"
          />
          <WorkTypeCard
            id="service"
            active={services.service}
            onToggle={() => onToggleService("service")}
            icon={<Wrench className="w-5 h-5" />}
            tone="teal"
            label="Service"
            sublabel="Scheduled / interval"
            hint="Routes to Tech Dashboard"
          />
          <WorkTypeCard
            id="diag"
            active={services.diag}
            onToggle={() => onToggleService("diag")}
            icon={<Stethoscope className="w-5 h-5" />}
            tone="amber"
            label="Diagnostic"
            sublabel="Concern / fault triage"
            hint="Routes to Tech Dashboard"
          />
        </div>

        {/* Combo hint banner */}
        <ComboHint services={services} />
      </section>

      {/* Free-text reason — always available */}
      <section className="rounded-lg border border-slate-200 bg-white p-5">
        <label className="block">
          <span className="block text-[12.5px] uppercase tracking-[0.18em] text-slate-700 font-semibold mb-2">
            Reason for visit (one-liner)
          </span>
          <textarea
            rows={2}
            placeholder="e.g. ‘MOT due next week, and there's a noise from the front when braking’"
            value={reasonText}
            onChange={(e) => onReasonText(e.target.value)}
            className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-[15px] text-slate-900 resize-none focus:outline-none focus:border-teal-500"
            data-testid="reason-summary-text"
          />
        </label>
      </section>

      {/* Companion panels — stacked, one per selected service */}
      {services.mot && (
        <MOTCompanion
          data={motData}
          onChange={onMotChange}
          vehicleReg={vehicleReg}
        />
      )}
      {services.service && (
        <ServiceCompanion data={serviceData} onChange={onServiceChange} />
      )}
      {services.diag && (
        <DiagnosticCompanion data={diagData} onChange={onDiagChange} />
      )}

      {!services.mot && !services.service && !services.diag && (
        <p
          className="text-[13.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3.5 py-2.5"
          data-testid="reason-empty-hint"
        >
          Pick at least one work type to start the intake.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// STAGE 4 — Review & Confirmation
// ---------------------------------------------------------------------------

function StageReview({
  customer,
  vehicle,
  services,
  reasonText,
  motData,
  serviceData,
  diagData,
}) {
  const [expectations, setExpectations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // iter211q — bump ``retryTick`` to re-fire the preview call after a
  // transient failure (backend restart, brief 5xx, network blip).
  const [retryTick, setRetryTick] = useState(0);

  // Build the same payload shape StartIntakePage will POST, plus pass the
  // raw diag fault tree so the engine sees structured data (not just the
  // stitched foh_notes text).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const services_requested = [];
    if (services.mot) services_requested.push("mot");
    if (services.service) services_requested.push("service");
    if (services.diag) services_requested.push("diag");

    const fault_tree = diagData?.fault_tree || null;
    const payload = {
      customer_name: customer.customer_name,
      make: vehicle.make,
      model: vehicle.model,
      mot_expiry_date: vehicle.mot_expiry_date,
      mot_status: vehicle.mot_status,
      services_requested,
      diag_fault_tree: fault_tree,
    };

    // iter211q — one silent retry on transient failure (backend
    // restart, connection reset, 5xx). The endpoint is a pure
    // deterministic function server-side so a retry is safe.
    const postWithRetry = async () => {
      try {
        return await axios.post(`${API}/intake/expectations/preview`, payload);
      } catch (e) {
        const status = e?.response?.status;
        // Only retry on network / server-side transients — never on
        // 4xx (that'd be a real payload problem the user needs to see).
        const isTransient = !status || status >= 500;
        if (!isTransient) throw e;
        await new Promise((res) => setTimeout(res, 600));
        return axios.post(`${API}/intake/expectations/preview`, payload);
      }
    };

    (async () => {
      try {
        const r = await postWithRetry();
        if (!cancelled) setExpectations(r.data);
      } catch (e) {
        if (!cancelled) {
          const status = e?.response?.status;
          const detail = e?.response?.data?.detail;
          // Give the FOH actionable text instead of the generic
          // "Could not preview recap" — they need to know whether
          // to retry, fix the payload, or call an engineer.
          let msg = detail;
          if (!msg) {
            if (!status) {
              msg = "Couldn't reach the server to build the recap. Check your connection and try again.";
            } else if (status >= 500) {
              msg = "The server had a hiccup building the recap. Give it another go.";
            } else {
              msg = "Could not preview recap.";
            }
          }
          setError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customer, vehicle, services, motData, serviceData, diagData, retryTick]);

  const serviceList = [];
  if (services.mot) serviceList.push("MOT");
  if (services.service) serviceList.push("Service");
  if (services.diag) serviceList.push("Diagnostic");

  return (
    <div className="space-y-4" data-testid="stage-review">
      <StageHeader
        tone="teal"
        icon={<Sparkles className="w-4 h-4" />}
        eyebrow="Stage 4 of 4"
        title="Review & confirm"
        subtitle="Quick verbal recap with the customer — then we open the workspace."
      />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
        {/* Left — Captured detail */}
        <div className="space-y-3">
          <ReviewBlock label="Customer" testId="review-customer">
            <ReviewRow k="Name" v={customer.customer_name || "—"} />
            <ReviewRow k="Phone" v={customer.contact_number || "—"} />
            <ReviewRow k="Email" v={customer.customer_email || "—"} />
          </ReviewBlock>

          <ReviewBlock label="Vehicle" testId="review-vehicle">
            <ReviewRow k="Reg" v={(vehicle.vehicle_reg || "—").toUpperCase()} mono />
            <ReviewRow
              k="Make / Model"
              v={[vehicle.make, vehicle.model].filter(Boolean).join(" ") || "—"}
            />
            <ReviewRow
              k="Year / Fuel"
              v={[vehicle.year, vehicle.fuel_type].filter(Boolean).join(" · ") || "—"}
            />
            {vehicle.mot_expiry_date && (
              <ReviewRow k="MOT expires" v={vehicle.mot_expiry_date} />
            )}
            {/* iter204 — Surface the physical-handover capture so FOH
                catches a mis-toggle before submit. */}
            <ReviewRow
              k="Vehicle on-site"
              v={vehicle.vehicle_on_site ? "Yes — keys with us" : "No — still with customer"}
            />
            {vehicle.vehicle_on_site && vehicle.key_tag_number && (
              <ReviewRow
                k="Key-tag #"
                v={String(vehicle.key_tag_number).trim()}
                mono
              />
            )}
          </ReviewBlock>

          <ReviewBlock
            label="Work selected"
            testId="review-work"
            accent={serviceList.length > 0 ? "teal" : "amber"}
          >
            {serviceList.length === 0 ? (
              <p className="text-[14px] text-amber-700">No services selected.</p>
            ) : (
              <p className="text-[15px] text-slate-900 font-semibold">
                {serviceList.join(" + ")}
              </p>
            )}
            {reasonText && (
              <p className="text-[13.5px] text-slate-600 italic mt-2 leading-snug">
                &ldquo;{reasonText}&rdquo;
              </p>
            )}
          </ReviewBlock>

          {services.mot && Object.keys(motData || {}).length > 0 && (
            <ReviewBlock label="MOT capture" testId="review-mot">
              {motData.mot_due_date && (
                <ReviewRow k="MOT due" v={motData.mot_due_date} />
              )}
              {motData.preferred_slot_window && (
                <ReviewRow k="Preferred slot" v={motData.preferred_slot_window} />
              )}
              {motData.customer_aware_of_advisories && (
                <ReviewRow
                  k="Aware of advisories"
                  v={motData.customer_aware_of_advisories}
                />
              )}
              {motData.last_advisories && (
                <ReviewRow k="Last advisories" v={motData.last_advisories} />
              )}
              {motData.pre_test_concerns && (
                <ReviewRow k="Pre-test concerns" v={motData.pre_test_concerns} />
              )}
            </ReviewBlock>
          )}

          {services.service && Object.keys(serviceData || {}).length > 0 && (
            <ReviewBlock label="Service capture" testId="review-service">
              {serviceData.service_type && (
                <ReviewRow k="Service type" v={serviceData.service_type} />
              )}
              {serviceData.current_mileage && (
                <ReviewRow k="Mileage" v={serviceData.current_mileage} />
              )}
              {serviceData.last_service_date && (
                <ReviewRow k="Last service" v={serviceData.last_service_date} />
              )}
              {serviceData.annual_mileage && (
                <ReviewRow k="Annual mileage" v={serviceData.annual_mileage} />
              )}
              {serviceData.collection_preference && (
                <ReviewRow
                  k="Collection"
                  v={serviceData.collection_preference}
                />
              )}
              {serviceData.concerns_text && (
                <ReviewRow k="Concerns" v={serviceData.concerns_text} />
              )}
            </ReviewBlock>
          )}

          {services.diag && Object.keys(diagData || {}).length > 0 && (
            <ReviewBlock label="Diagnostic capture" testId="review-diag">
              {diagData.template && (
                <ReviewRow
                  k="Template"
                  v={diagData.template.replace(/_/g, " ")}
                />
              )}
              {diagData.customer_concern && (
                <ReviewRow
                  k="Customer’s words"
                  v={diagData.customer_concern}
                />
              )}
              {diagData.fault_tree &&
                Object.entries(diagData.fault_tree)
                  .filter(([, v]) => v && (!Array.isArray(v) || v.length > 0))
                  .map(([k, v]) => (
                    <ReviewRow
                      key={k}
                      k={k.replace(/_/g, " ")}
                      v={Array.isArray(v) ? v.join(", ") : String(v).replace(/_/g, " ")}
                    />
                  ))}
            </ReviewBlock>
          )}
        </div>

        {/* Right — Customer Intake Summary (deterministic recap) */}
        <div className="lg:sticky lg:top-4 lg:self-start">
          {loading ? (
            <div
              className="rounded-lg border border-slate-200 bg-white p-4 text-[14px] text-slate-500 inline-flex items-center"
              data-testid="review-recap-loading"
            >
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Building customer recap…
            </div>
          ) : error ? (
            <div
              className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-[14px] text-rose-800 space-y-2"
              data-testid="review-recap-error"
            >
              <p>{error}</p>
              <button
                type="button"
                onClick={() => setRetryTick((n) => n + 1)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md bg-rose-700 hover:bg-rose-800 text-white transition-colors"
                data-testid="review-recap-retry"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Try again
              </button>
            </div>
          ) : (
            <CustomerIntakeSummary
              mode="preview"
              expectations={expectations}
              testIdPrefix="review-recap"
              showWatchOuts
            />
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewBlock({ label, testId, accent = "slate", children }) {
  const ring =
    accent === "teal"
      ? "border-teal-200"
      : accent === "amber"
      ? "border-amber-200"
      : "border-slate-200";
  return (
    <section
      className={`rounded-lg border bg-white ${ring}`}
      data-testid={testId}
    >
      <header className="px-4 pt-3 pb-1.5 border-b border-slate-100">
        <p className="text-[11.5px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
          {label}
        </p>
      </header>
      <div className="px-4 py-3 space-y-1.5">{children}</div>
    </section>
  );
}

function ReviewRow({ k, v, mono = false }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 text-[14px]">
      <span className="text-slate-500 capitalize">{k}</span>
      <span className={`text-slate-900 ${mono ? "font-mono tracking-wider" : ""}`}>
        {v}
      </span>
    </div>
  );
}


function WorkTypeCard({
  id,
  active,
  onToggle,
  icon,
  tone,
  label,
  sublabel,
  hint,
}) {
  const accent = WORK_TYPE_ACCENT[tone] || WORK_TYPE_ACCENT.teal;
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`relative text-left rounded-lg border-2 p-3.5 transition-all ${
        active
          ? `${accent.borderOn} ${accent.bgOn} shadow-sm`
          : "border-slate-200 bg-white hover:border-slate-300"
      }`}
      data-testid={`work-type-${id}`}
      data-active={active ? "true" : "false"}
    >
      <div className="flex items-start gap-2.5">
        <div
          className={`w-9 h-9 rounded-md flex items-center justify-center ${
            active ? accent.iconBgOn : "bg-slate-100"
          } ${active ? accent.iconColorOn : "text-slate-500"}`}
        >
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <p className={`text-[15px] font-bold ${active ? accent.textOn : "text-slate-800"}`}>
            {label}
          </p>
          <p className="text-[12.5px] text-slate-500 leading-snug">{sublabel}</p>
          <p
            className={`text-[11px] uppercase tracking-wider mt-1 font-semibold ${
              active ? accent.hintOn : "text-slate-400"
            }`}
          >
            {hint}
          </p>
        </div>
        <div
          className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
            active ? `${accent.borderOn} ${accent.iconBgOn}` : "border-slate-300 bg-white"
          }`}
        >
          {active && <Check className={`w-3 h-3 ${accent.iconColorOn}`} strokeWidth={3} />}
        </div>
      </div>
    </button>
  );
}

function ComboHint({ services }) {
  const list = [];
  if (services.mot) list.push("MOT");
  if (services.service) list.push("Service");
  if (services.diag) list.push("Diagnostic");
  if (list.length < 2) return null;

  let msg = null;
  if (services.mot && services.service && services.diag) {
    msg =
      "Triple combo — MOT slot + parallel Service + Diagnostic allocations.";
  } else if (services.mot && services.service) {
    msg =
      "MOT + Service — half-price MOT applies when booked alongside a service.";
  } else if (services.mot && services.diag) {
    msg =
      "MOT + Diagnostic — tester runs the MOT, technician opens the diag bay in parallel.";
  } else if (services.service && services.diag) {
    msg =
      "Service + Diagnostic — diag triage runs alongside the service so we don't double-handle the vehicle.";
  }

  return (
    <p
      className="mt-3 text-[13px] text-teal-800 bg-teal-50 border border-teal-200 rounded-md px-3 py-2"
      data-testid="reason-combo-hint"
    >
      <span className="font-semibold">{list.join(" + ")}.</span> {msg}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Atoms
// ---------------------------------------------------------------------------

function StageHeader({ tone, icon, eyebrow, title, subtitle }) {
  const bg = TONE_HEADER[tone] || TONE_HEADER.teal;
  return (
    <div
      className={`relative overflow-hidden rounded-xl px-5 py-4 text-white ${bg}`}
      data-testid="stage-header"
    >
      <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top_right,_white_0%,_transparent_55%)]" />
      <div className="absolute -bottom-8 -right-8 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
      <div className="relative flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-white/20 border border-white/30 flex items-center justify-center">
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-[10.5px] uppercase tracking-[0.22em] font-semibold opacity-80">
            {eyebrow}
          </p>
          <h2 className="text-[20px] font-bold leading-tight">{title}</h2>
          {subtitle && (
            <p className="text-[13px] opacity-90 leading-snug mt-0.5 max-w-[60ch]">
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function FieldShell({ label, icon, accessory, required, children }) {
  return (
    <label className="group block rounded-lg border border-slate-200 bg-white px-3.5 py-2 shadow-sm transition-all focus-within:border-teal-500 focus-within:ring-2 focus-within:ring-teal-100 hover:border-slate-300">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.14em] text-slate-700 font-semibold">
          {icon && <span className="text-teal-600">{icon}</span>}
          {label}
          {required && <span className="text-rose-500">*</span>}
        </span>
        {accessory}
      </div>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function VehicleField({ label, value }) {
  if (!value) return null;
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className="text-[11.5px] uppercase tracking-wider text-slate-500">
        {label}
      </span>
      <span className="text-slate-900 font-semibold">{value}</span>
    </span>
  );
}

function formatMotDate(raw) {
  if (!raw) return "—";
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return raw;
  }
}

// ---------------------------------------------------------------------------
// Stitch all reason-stage capture into a single foh_notes blob so the
// session workspace surfaces everything immediately.
// ---------------------------------------------------------------------------
function composeFohNotes({ reasonText, services, motData, serviceData, diagData }) {
  const parts = [];
  if (reasonText?.trim()) parts.push(`Reason: ${reasonText.trim()}`);

  if (services.mot && Object.keys(motData || {}).length) {
    const lines = [];
    if (motData.mot_due_date) lines.push(`MOT due: ${motData.mot_due_date}`);
    if (motData.preferred_slot_window) lines.push(`Slot: ${motData.preferred_slot_window}`);
    if (motData.last_advisories) lines.push(`Last advisories: ${motData.last_advisories}`);
    if (motData.customer_aware_of_advisories)
      lines.push(`Customer aware: ${motData.customer_aware_of_advisories}`);
    if (motData.pre_test_concerns) lines.push(`Pre-test concerns: ${motData.pre_test_concerns}`);
    if (lines.length) parts.push(`— MOT —\n${lines.join("\n")}`);
  }

  if (services.service && Object.keys(serviceData || {}).length) {
    const lines = [];
    if (serviceData.service_type) lines.push(`Type: ${serviceData.service_type}`);
    if (serviceData.current_mileage) lines.push(`Mileage: ${serviceData.current_mileage}`);
    if (serviceData.last_service_date)
      lines.push(`Last service: ${serviceData.last_service_date}`);
    if (serviceData.annual_mileage) lines.push(`Annual mileage: ${serviceData.annual_mileage}`);
    if (serviceData.concerns_text) lines.push(`Concerns: ${serviceData.concerns_text}`);
    if (serviceData.collection_preference)
      lines.push(`Collection: ${serviceData.collection_preference}`);
    if (lines.length) parts.push(`— Service —\n${lines.join("\n")}`);
  }

  if (services.diag && Object.keys(diagData || {}).length) {
    const lines = [];
    if (diagData.template) lines.push(`Template: ${diagData.template}`);
    if (diagData.customer_concern) lines.push(`Concern: ${diagData.customer_concern}`);
    const ft = diagData.fault_tree || {};
    const ftLines = [];
    if (ft.lamp_colour) ftLines.push(`Lamp colour: ${ft.lamp_colour}`);
    if (ft.lamp_identity) ftLines.push(`Lamp: ${ft.lamp_identity}`);
    if (ft.lamp_behaviour) ftLines.push(`Behaviour: ${ft.lamp_behaviour}`);
    if (ft.driveable) ftLines.push(`Driveable: ${ft.driveable}`);
    if (Array.isArray(ft.accompanying_symptoms) && ft.accompanying_symptoms.length)
      ftLines.push(`Symptoms: ${ft.accompanying_symptoms.join(", ")}`);
    if (ft.recent_trigger) ftLines.push(`Recent trigger: ${ft.recent_trigger}`);
    if (ft.time_since_first_seen) ftLines.push(`First seen: ${ft.time_since_first_seen}`);
    if (ft.miles_since_first_seen)
      ftLines.push(`Miles since: ${ft.miles_since_first_seen}`);
    if (ft.notes) ftLines.push(`Notes: ${ft.notes}`);
    if (ftLines.length) lines.push(`Fault tree:\n  ${ftLines.join("\n  ")}`);
    if (lines.length) parts.push(`— Diagnostic —\n${lines.join("\n")}`);
  }

  return parts.join("\n\n");
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TONE_HEADER = {
  blue: "bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700",
  amber: "bg-gradient-to-r from-amber-600 via-orange-600 to-rose-600",
  teal: "bg-gradient-to-r from-teal-600 via-teal-700 to-emerald-700",
};

const WORK_TYPE_ACCENT = {
  blue: {
    borderOn: "border-blue-500",
    bgOn: "bg-blue-50",
    iconBgOn: "bg-blue-600",
    iconColorOn: "text-white",
    textOn: "text-blue-900",
    hintOn: "text-blue-700",
  },
  teal: {
    borderOn: "border-teal-500",
    bgOn: "bg-teal-50",
    iconBgOn: "bg-teal-600",
    iconColorOn: "text-white",
    textOn: "text-teal-900",
    hintOn: "text-teal-700",
  },
  amber: {
    borderOn: "border-amber-500",
    bgOn: "bg-amber-50",
    iconBgOn: "bg-amber-600",
    iconColorOn: "text-white",
    textOn: "text-amber-900",
    hintOn: "text-amber-700",
  },
};
